import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { useToast } from '../hooks/use-toast';

const BidsTab = ({ searchTerm, selectedCategory, user }) => {
  const [liveBids, setLiveBids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTrade, setSelectedTrade] = useState('all');
  const { toast } = useToast();

  const trades = [
    'Installation', 'Plumbing Installation', 'HVAC Installation', 'Roofing Installation',
    'Electrical Installation', 'Carpentry', 'Landscaping', 'Painting', 'Drywall Installation',
    'Software Engineering', 'Interior Design', 'Real Estate Sales'
  ];

  const fetchLiveBids = async () => {
    try {
      const token = localStorage.getItem('dcTech_token');
      const params = new URLSearchParams();
      
      if (selectedTrade !== 'all') params.append('trade', selectedTrade);
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/bids/live?${params}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.ok) {
        const bidsData = await response.json();
        setLiveBids(bidsData);
      } else {
        throw new Error('Failed to fetch live bids');
      }
    } catch (error) {
      console.error('Error fetching live bids:', error);
      toast({
        title: "Error",
        description: "Failed to load live bids. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLiveBids();
  }, [selectedTrade]);

  const filteredBids = liveBids.filter(bid => {
    const matchesSearch = searchTerm === '' || 
      bid.jobDetails?.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      bid.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      bid.providerDetails?.name?.toLowerCase().includes(searchTerm.toLowerCase());
      
    const matchesCategory = selectedCategory === 'all' || 
      bid.jobDetails?.title?.toLowerCase().includes(selectedCategory.toLowerCase());
      
    return matchesSearch && matchesCategory;
  });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getUrgencyColor = (urgency) => {
    switch (urgency) {
      case 'urgent': return 'bg-red-500';
      case 'normal': return 'bg-blue-500';
      case 'flexible': return 'bg-green-500';
      default: return 'bg-gray-500';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-white text-lg">Loading live bids...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Information for Service Providers */}
      {user?.userType === 'provider' && (
        <div className="bg-green-600/20 border border-green-400/30 rounded-lg p-4 mb-6">
          <h3 className="text-green-300 font-semibold mb-2">💼 For Service Providers</h3>
          <p className="text-green-200 text-sm">
            Browse live job postings from customers. Click "Place Bid" to submit your proposal for any job that matches your expertise.
          </p>
        </div>
      )}

      {/* Information for Customers */}
      {user?.userType === 'customer' && (
        <div className="bg-blue-600/20 border border-blue-400/30 rounded-lg p-4 mb-6">
          <h3 className="text-blue-300 font-semibold mb-2">🏠 For Customers</h3>
          <p className="text-blue-200 text-sm">
            View active job postings and track bids from service providers. To post a new job, go to Profile → Jobs → Post New Job.
          </p>
        </div>
      )}

      {/* Trade Filter */}
      <div className="mb-6">
        <h3 className="text-white text-lg font-semibold mb-3">Filter by Trade</h3>
        <div className="flex flex-wrap gap-2">
          <Badge 
            variant={selectedTrade === 'all' ? 'default' : 'outline'}
            className={`cursor-pointer px-4 py-2 ${
              selectedTrade === 'all' 
                ? 'bg-blue-500 text-white' 
                : 'border-white/50 text-white bg-white/10'
            }`}
            onClick={() => setSelectedTrade('all')}
          >
            All Trades
          </Badge>
          {trades.map(trade => (
            <Badge 
              key={trade}
              variant={selectedTrade === trade ? 'default' : 'outline'}
              className={`cursor-pointer px-4 py-2 ${
                selectedTrade === trade 
                  ? 'bg-blue-500 text-white' 
                  : 'border-white/50 text-white bg-white/10'
              }`}
              onClick={() => setSelectedTrade(trade)}
            >
              {trade}
            </Badge>
          ))}
        </div>
      </div>

      {/* Live Bids Grid */}
      {filteredBids.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-white/60 text-lg mb-4">No live bids found</div>
          <p className="text-white/40">
            {selectedTrade !== 'all' 
              ? `No bids available for ${selectedTrade}` 
              : 'Check back later for new bidding opportunities'
            }
          </p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredBids.map(bid => (
            <Card key={bid._id} className="bg-white/10 backdrop-blur-lg border-white/20 bg-white/10 ">
              <CardHeader>
                <div className="flex justify-between items-start mb-2">
                  <CardTitle className="text-white text-lg line-clamp-2">
                    {bid.jobDetails?.title}
                  </CardTitle>
                  <Badge className={`${getUrgencyColor(bid.jobDetails?.urgency)} text-white text-xs`}>
                    {bid.jobDetails?.urgency || 'normal'}
                  </Badge>
                </div>
                
                <div className="flex items-center gap-2 mb-3">
                  <img 
                    src={bid.providerDetails?.image || '/api/placeholder/40/40'} 
                    alt={bid.providerDetails?.name}
                    className="w-10 h-10 rounded-full"
                  />
                  <div>
                    <p className="text-white font-medium">{bid.providerDetails?.name}</p>
                    <p className="text-white/60 text-sm">
                      ⭐ {bid.providerDetails?.rating || 4.5} ({bid.providerDetails?.reviews || 0} reviews)
                    </p>
                  </div>
                </div>
              </CardHeader>
              
              <CardContent>
                <div className="space-y-3">
                  {/* Job Details */}
                  <div>
                    <p className="text-white/80 text-sm line-clamp-2 mb-2">
                      {bid.jobDetails?.description}
                    </p>
                    <div className="flex items-center gap-4 text-white/60 text-xs">
                      <span>📍 {bid.jobDetails?.location}</span>
                      {bid.jobDetails?.budget && (
                        <span>💰 {formatCurrency(bid.jobDetails.budget)}</span>
                      )}
                    </div>
                  </div>

                  {/* Bid Details */}
                  <div className="border-t border-white/10 pt-3">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-white/60 text-sm">Bid Amount:</span>
                      <span className="text-green-400 font-bold">
                        {formatCurrency(bid.bidAmount)}
                      </span>
                    </div>
                    
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-white/60 text-sm">Duration:</span>
                      <span className="text-white text-sm">{bid.estimatedDuration}</span>
                    </div>
                    
                    <div className="flex justify-between items-center mb-3">
                      <span className="text-white/60 text-sm">Available:</span>
                      <span className="text-white text-sm">{bid.availableDate}</span>
                    </div>
                    
                    <p className="text-white/70 text-sm line-clamp-2 mb-3">
                      {bid.description}
                    </p>
                  </div>

                  {/* Timestamp */}
                  <div className="text-white/40 text-xs text-right">
                    Posted {formatDate(bid.createdAt)}
                  </div>

                  {/* Action Button - Only show to job owner */}
                  {user?.userType === 'customer' && (
                    <div className="pt-2">
                      <Button 
                        className="w-full bg-gradient-to-r from-green-500 to-blue-500   text-white"
                        onClick={() => {
                          toast({
                            title: "Contact Provider",
                            description: "Contact functionality will be available soon.",
                          });
                        }}
                      >
                        Contact Provider
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default BidsTab;